import { ReactNode, lazy } from 'react';
import _ from 'lodash';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThingPort, WebThing } from '@API/WebThing';
import { WidgetLocalizationKeys, LazyWidget, IconURIPath } from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { IsNullOrEmpty } from '@Utils/Micro';
import { PortKeys, PdfViewerData, ePdfViewerTheme } from './PdfViewerStructs';
// --------------------------------------------------------------------------------
const PdfViewer = lazy(() => import('./PdfViewerAsync'));
const PdfViewerSettings = lazy(() => import('./PdfViewerAsync').then(module => ({ default: module.PdfViewerSettings })));
// --------------------------------------------------------------------------------
export class PdfViewerWidget extends IWidgetThingsClass<PdfViewerData> {
    // --------------------------------------------------------------------------------
    Render(): ReactNode {
        return (
            <LazyWidget>
                <PdfViewer />
            </LazyWidget>
        );
    }
    // --------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactNode {
        return (
            <LazyWidget>
                <PdfViewerSettings
                    key={'settings-' + key}
                    NodeKey={this.NodeKey}
                    ThingKey={this.ThingKey}
                    width={width}
                    height={height}
                    WidgetData={_.cloneDeep(this.WidgetData)}
                    WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                    onWidgetDataUpdate={data => this.WidgetDataUpdate(data)}
                    SettingListToolbar={this.Parent?.SettingsListToolbar} />
            </LazyWidget>
        );
    }
    // --------------------------------------------------------------------------------
    get ports(): WebThingPort[] {
        return [
            new WebThingPort({
                Name: 'File URL',
                PortKey: PortKeys.FileUrl,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            }, WebThingPortType.Data)
        ];
    }
    // --------------------------------------------------------------------------------
    initThing() {
        let webThing = new WebThing(
            this.WebModule,
            WidgetDescriptionToPlegmaThing(this.Description),
            this.ports
        );
        this.ThingKey = webThing.ThingKey;
    }
    // --------------------------------------------------------------------------------
    updateThing() {
        let node = WebNode.GetInstanceKey(this.NodeKey);
        let thing = node.GetThing(this.ThingKey);

        thing.Ports = {};
        _.forEach(this.ports, p => thing.AddPort(p));

        node.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // --------------------------------------------------------------------------------
    GetDefaultWidgetData(): PdfViewerData {
        return PdfViewerWidget.GetDefaultWidgetDataStatic();
    }
    // --------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): PdfViewerData {
        return { theme: ePdfViewerTheme.light } as PdfViewerData;
    }
    // --------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            MinW: 1,
            MinH: 1,
            Key: 'PdfViewerWidget',
            Type: 'PdfViewerWidget',
            Title: 'PDF Viewer',
            Data: PdfViewerWidget.GetDefaultWidgetDataStatic(),
            IconURI: IconURIPath + '/svg/tile.svg'
        };
    }
    // --------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        return { valid: true, errors: '' };
    }
    // --------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        let dict = {};
        let widgetTitle = this.Description.WidgetTitle;

        if (!IsNullOrEmpty(widgetTitle))
            dict[WidgetLocalizationKeys.Title] = widgetTitle;
        return dict;
    }
    // --------------------------------------------------------------------------------
}